<script setup lang="ts">
import type { DisciplineCode } from '@brighterly/lib-core-types/student-assessment/types'

import { faXmark } from '@fortawesome/pro-solid-svg-icons'

import { AppText } from '@shared/ui/typography'
import { DropZoneArea } from '@shared/ui/other'
import { CardAdmin } from '@shared/ui/cards'
import { BtnIcon, BtnPrimary } from '@shared/ui/buttons'

import { useImportCurriculum, CURRICULUM_ACCEPT } from '../../../model/tools/curriculum/import-curriculum.hook'

defineOptions({
  name: 'ImportCurriculumCard',
})

const props = defineProps<ImportCurriculumCardProps>()

interface ImportCurriculumCardProps {
  disciplineCode: DisciplineCode
  title: string
}

const {
  refFile,
  refIsUploading,
  refProgress,
  refError,
  selectFiles,
  submit,
  reset,
} = useImportCurriculum(props.disciplineCode)
</script>

<template>
  <CardAdmin
    :title="title"
    subtitle="Upload a CSV file to add a new curriculum version."
  >
    <template #header>
      <BtnPrimary
        :text="refIsUploading ? 'Importing…' : 'Import'"
        color="blue"
        :disabled="!refFile || refIsUploading"
        @click="submit"
      />
    </template>

    <div class="import-curriculum-card">
      <DropZoneArea
        :accept="CURRICULUM_ACCEPT"
        :multiple="false"
        :disabled="refIsUploading"
        title="Drag & drop a curriculum file here"
        browse-text="Browse file"
        @files="selectFiles"
      />

      <div
        v-if="refFile"
        class="import-curriculum-card__file"
      >
        <AppText name="body2-medium">
          {{ refFile.name }}
        </AppText>
        <BtnIcon
          :icon="faXmark"
          view="transparent"
          :disabled="refIsUploading"
          @click="reset"
        />
      </div>

      <AppText
        v-if="refIsUploading"
        name="body2"
        color="slate-500"
      >
        Uploading… {{ refProgress }}%
      </AppText>

      <AppText
        v-if="refError"
        name="body2"
        color="red-500"
      >
        {{ refError }}
      </AppText>
    </div>
  </CardAdmin>
</template>

<style scoped lang="scss" src="./ImportCurriculumCard.scss" />
